import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Plus, Calendar, Users, Clock } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import axios from "axios";
import {
  attendanceColumns,
  AttendanceEmployee,
} from "../lib/attendanceTableColumns";
import { SimpleDataTable } from "../components/simpleDataTable";
import { Spinner } from "../components/ui/shadcn-io/spinner";

export default function Attendance() {
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = useState<AttendanceEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  // Current month and year as defaults
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    (currentDate.getMonth() + 1).toString()
  );
  const [selectedYear, setSelectedYear] = useState(
    currentDate.getFullYear().toString()
  );

  const [stats, setStats] = useState({
    total: 0,
    attendanceAdded: 0,
    attendanceNotAdded: 0,
  });

  // Show success message if redirected from add/edit attendance page
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState(null, "");

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  // Fetch data when month/year changes
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      fetchEmployeesWithAttendance();
    }
  }, [selectedMonth, selectedYear]);

  const fetchEmployeesWithAttendance = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");

      // Use the backend endpoint that filters employees appropriately
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/attendance/month/${selectedYear}/${selectedMonth}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const employeesData = response.data;

      // Map backend data to frontend AttendanceEmployee type
      const mappedEmployees: AttendanceEmployee[] = employeesData.map(
        (emp: any) => ({
          id: emp._id,
          name: emp.fullName,
          email: emp.email,
          phone: emp.phone,
          company: emp.company,
          designation: emp.designation,
          department: emp.department,
          attendanceStatus: emp.attendanceStatus,
          isActive: emp.isActive !== undefined ? emp.isActive : true,
          resignationDate: emp.resignationDate,
          attendance: emp.attendance,
        })
      );

      setEmployees(mappedEmployees);

      // Update stats
      setStats({
        total: mappedEmployees.length,
        attendanceAdded: mappedEmployees.filter(
          (emp) => emp.attendanceStatus === "added"
        ).length,
        attendanceNotAdded: mappedEmployees.filter(
          (emp) => emp.attendanceStatus === "not_added"
        ).length,
      });
    } catch (error: any) {
      console.error("Error fetching employees with attendance:", error);
      setError(
        error.response?.data?.message ||
          "Failed to fetch employees with attendance"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns = attendanceColumns(selectedMonth, selectedYear);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
          <p className="text-muted-foreground">
            Track and manage employee attendance records
          </p>
        </div>

        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center space-y-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div>
              <h3 className="text-lg font-medium">Loading attendance...</h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we fetch the data
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
        <p className="text-muted-foreground">
          Track and manage employee attendance records
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-green-500" />
            <p className="text-sm font-medium text-green-800">Success</p>
          </div>
          <p className="mt-1 text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">Error</p>
          </div>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Filter Section */}
      <div className="flex items-end gap-4 rounded-lg border bg-card p-4">
        <div className="flex-1 space-y-2">
          <Label htmlFor="month">Month</Label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger id="month">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">January</SelectItem>
              <SelectItem value="2">February</SelectItem>
              <SelectItem value="3">March</SelectItem>
              <SelectItem value="4">April</SelectItem>
              <SelectItem value="5">May</SelectItem>
              <SelectItem value="6">June</SelectItem>
              <SelectItem value="7">July</SelectItem>
              <SelectItem value="8">August</SelectItem>
              <SelectItem value="9">September</SelectItem>
              <SelectItem value="10">October</SelectItem>
              <SelectItem value="11">November</SelectItem>
              <SelectItem value="12">December</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 space-y-2">
          <Label htmlFor="year">Year</Label>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger id="year">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Employees
              </p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            For {selectedMonth}/{selectedYear}
          </p>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Attendance Recorded
              </p>
              <p className="text-2xl font-bold">{stats.attendanceAdded}</p>
            </div>
            <Calendar className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Completed records
          </p>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Pending Records
              </p>
              <p className="text-2xl font-bold">{stats.attendanceNotAdded}</p>
            </div>
            <Clock className="h-8 w-8 text-red-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">Needs attention</p>
        </div>
      </div>

      {/* Data Table */}
      <SimpleDataTable columns={columns} data={employees} />
    </div>
  );
}
